import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

/**
 * What the offer editor's pages need from the text editor besides addictive-richtext (D-150):
 * the pages' gaps. The fields' values ({{key}}), the changes since a revision, the spelling, the
 * paragraph style, the emphasis colour and redrawing decorations are the library's (FieldValues,
 * ChangeMarks, SpellMarks, ParagraphStyle, TextColor, refreshDecorations).
 */

/**
 * Room between two lines of a paragraph or a list item that crosses a page (D-191): the
 * paragraph goes on at the top of the next page, as the PDF and Word break it. The line that
 * moves is lowered by the room (vertical-align): its line box grows downward by just that, its
 * text lands at the next page's top, and nothing wraps otherwise — no block between the lines and
 * no inline block, either of which changes how a full line wraps. Its first character carries
 * the room (.pg-line-gap, data-px), the rest of the line only the lowering.
 *
 * And a paragraph, a heading or a list item of the text pushed to the next page whole (its room
 * above it: .pg-push, data-pg-push). Both are the page's pagination's (settle in DocumentCanvas),
 * nothing in the text — and both stay with what they belong to while the text is typed: a
 * paragraph added before one at a page's top never takes its room (a new line written after
 * Enter stays on the page it was typed on), until the pages are measured again.
 */
const gapsKey = new PluginKey('pageGaps')
function pageDecorations(doc, gaps, pushes) {
  const size = doc.content.size
  const found = []
  for (const g of gaps) {
    if (!(g.pos > 0 && g.end > g.pos && g.end <= size && g.px > 0)) continue
    // Pages side by side (D-204): where the line moves to the next page, the next column starts instead.
    found.push(Decoration.widget(g.pos, () => Object.assign(document.createElement('span'), { className: 'pg-col-break' }), { side: -1, key: `col:${g.pos}` }))
    found.push(Decoration.inline(g.pos, g.pos + 1, { class: 'pg-line-gap', 'data-px': String(g.px), style: `vertical-align:-${g.px}px` }))
    if (g.end > g.pos + 1) found.push(Decoration.inline(g.pos + 1, g.end, { class: 'pg-line-moved', style: `vertical-align:-${g.px}px` }))
  }
  for (const p of pushes) {
    const node = p.from >= 0 && p.from < size ? doc.nodeAt(p.from) : null
    if (!node || !(p.px > 0)) continue
    found.push(Decoration.node(p.from, p.from + node.nodeSize, { class: 'pg-push', 'data-pg-push': String(p.px), style: `--pg-push:${p.px}px` }))
  }
  return found.length ? DecorationSet.create(doc, found) : DecorationSet.empty
}
export const PageGaps = Extension.create({
  name: 'pageGaps',
  addProseMirrorPlugins() {
    return [new Plugin({
      key: gapsKey,
      state: {
        init: () => DecorationSet.empty,
        // Set by the pagination; otherwise carried along with the text as it changes.
        apply: (tr, set) => {
          const given = tr.getMeta(gapsKey)
          return given ? pageDecorations(tr.doc, given.gaps, given.pushes) : set.map(tr.mapping, tr.doc)
        },
      },
      props: { decorations: (state) => gapsKey.getState(state) },
    })]
  },
})
/** The pagination's room in a text: between a paragraph's lines (`gaps`) and above whole ones (`pushes`). */
export function setPageGaps(editor, gaps, pushes) {
  if (editor && !editor.isDestroyed) editor.view.dispatch(editor.state.tr.setMeta(gapsKey, { gaps, pushes }).setMeta('addToHistory', false))
}
